import { ComponentType, ReactNode, useState } from 'react'

import { IconFilter2, IconFilter2Count } from '@avantstay/backoffice-vectors'

import {
  FloatingPortal,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react'

import * as S from './FilterIndicator.styles'
import { FilterContainer } from './FilterIndicator.styles'

export interface FilterComponentProps {
  onClose: () => void
}

interface FilterIndicatorProps {
  alwaysVisible?: boolean
  filterClassName?: string
  filterComponent: ReactNode | ComponentType<FilterComponentProps>
  filterCount?: number
  filterPlacement?: Placement
  isFilterActive?: boolean
  onToggleFilter?: (visible: boolean) => void
}

export const FilterIndicator = ({
  alwaysVisible,
  filterClassName,
  filterComponent: FilterComponent,
  filterCount,
  filterPlacement = 'bottom',
  isFilterActive,
  onToggleFilter,
}: FilterIndicatorProps) => {
  const filterHasLength = filterCount ? filterCount > 0 : false
  const [filterVisible, setFilterVisible] = useState(false)
  const handleShow = () => {
    setFilterVisible(true)
    onToggleFilter?.(true)
  }
  const handleHide = () => {
    setTimeout(() => {
      setFilterVisible(false)
      onToggleFilter?.(false)
    }, 10) // this is delayed to fire internal filterComponent onClickOut earlier
  }

  const { refs, floatingStyles, context } = useFloating({
    open: filterVisible,
    onOpenChange: shouldOpen => (shouldOpen ? handleShow() : handleHide()),
    placement: filterPlacement,
    middleware: [offset({ mainAxis: 10, crossAxis: 10 }), shift({ padding: 8 })],
  })

  const click = useClick(context)
  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss])

  return (
    <S.Root alwaysVisible={alwaysVisible || isFilterActive || filterVisible}>
      {filterVisible && (
        <FloatingPortal>
          <FilterContainer
            className={filterClassName}
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
          >
            {typeof FilterComponent === 'function' ? (
              <FilterComponent onClose={handleHide} />
            ) : (
              FilterComponent
            )}
          </FilterContainer>
        </FloatingPortal>
      )}
      <S.IconWrapper
        $isFilterActive={isFilterActive || filterHasLength || filterVisible}
        ref={refs.setReference}
        {...getReferenceProps()}
      >
        {filterHasLength ? (
          <>
            <IconFilter2Count />
            <S.ActiveFilterIndicator>{filterCount}</S.ActiveFilterIndicator>
          </>
        ) : (
          <IconFilter2 />
        )}
      </S.IconWrapper>
    </S.Root>
  )
}
